import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import {
  Defaults,
  type InfiniteLoaderListProps as InfiniteLoaderListPropsType,
} from '@pbt/pbt-ui-components'

import type { PuiDataTableColumn } from '~/components/common/lists/pui-data-table/puiDataTableType'
import {
  fetchInvoicePage,
  fetchMoreItemsForClientInvoiceActivity,
  getClientBillingActivityInvoiceError,
  getInvoiceLoading,
  getInvoicePageData,
  getInvoiceTotalCount,
  getShouldRefreshData,
  getUnpaidInvoiceLoading,
  getUnpaidInvoicePageData,
  getUnpaidInvoiceTotalCount,
} from '~/store/duck/clientBillingActivityData'

import {
  getExpandedWidgetLimit,
  getExpandedWidgetOffset,
} from '../table/common/expandedWidgetUtils'
import { useGetInvoiceBillingActivityTableColumns } from '../table/invoices/columns'
import {
  ClientBillingActivityInfiniteLoaderList,
  type ClientBillingActivityInfiniteLoaderListClassesType,
} from './ClientBillingActivityInfiniteLoaderList'
import { useFetchOnInitialSyncQueryParams } from './hooks'

interface InvoicesInfiniteLoaderListProps {
  InfiniteLoaderListProps?: Partial<InfiniteLoaderListPropsType>
  classes?: ClientBillingActivityInfiniteLoaderListClassesType
  clientId: string | undefined
  hasError?: boolean
  initialItem?: number
  invoiceStateIds?: string[]
  onCheckInvoices?: (id: string) => void
  refundStateIds?: string[]
  selectedInvoiceIds?: string[]
  showDueToPay?: boolean
  sortColumns?: (columns: PuiDataTableColumn[]) => PuiDataTableColumn[]
  supportBiDirectionalInfiniteScroll?: boolean
  unpaid: boolean
}

export const InvoicesInfiniteLoaderList = ({
  InfiniteLoaderListProps,
  classes,
  clientId,
  hasError = false,
  invoiceStateIds = [],
  refundStateIds = [],
  selectedInvoiceIds,
  showDueToPay,
  unpaid,
  sortColumns,
  onCheckInvoices,
  initialItem: initialItemParam = 0,
  supportBiDirectionalInfiniteScroll = false,
}: InvoicesInfiniteLoaderListProps) => {
  const dispatch = useDispatch()
  const { t } = useTranslation('Common')

  const refresh = useSelector(getShouldRefreshData)
  const invoicesPageData = useSelector(getInvoicePageData)
  const invoicesTotalCount = useSelector(getInvoiceTotalCount)
  const isLoading = useSelector(getInvoiceLoading)
  const unpaidInvoicesPageData = useSelector(getUnpaidInvoicePageData)
  const unpaidInvoicesTotalCount = useSelector(getUnpaidInvoiceTotalCount)
  const isLoadingUnpaidInvoices = useSelector(getUnpaidInvoiceLoading)
  const error = useSelector(getClientBillingActivityInvoiceError)

  const [offsetValue, setOffsetValue] = useState(0)

  const columns = useGetInvoiceBillingActivityTableColumns({
    key: 'value',
    checkbox: onCheckInvoices
      ? {
          hasError,
          onCheck: onCheckInvoices,
          getIsChecked: (id) => (selectedInvoiceIds || []).includes(id),
        }
      : undefined,
    dueToPayPrimary: showDueToPay,
    supportBiDirectionalInfiniteScroll,
  }) as PuiDataTableColumn[]
  const sortedColumns = sortColumns ? sortColumns(columns) : columns

  const handleFetchMore = ({ from, to }: { from: number; to: number }) => {
    if (clientId) {
      const offset = from < 10 && supportBiDirectionalInfiniteScroll ? 0 : from
      const limit =
        to && supportBiDirectionalInfiniteScroll
          ? to - offset + 1
          : Defaults.INFINITE_LIST_BATCH_LOAD_COUNT
      setOffsetValue(offset)
      dispatch(
        fetchMoreItemsForClientInvoiceActivity({
          clientId,
          from: offset,
          to: limit,
          refundStateIds,
          stateIds: invoiceStateIds,
          unpaid,
        }),
      )
    }
  }

  useEffect(() => {
    if (clientId && refresh) {
      const limit = initialItemParam
        ? Defaults.INFINITE_LIST_BATCH_LOAD_COUNT * 2
        : Defaults.INFINITE_LIST_BATCH_LOAD_COUNT
      dispatch(
        fetchInvoicePage({
          unpaid,
          clientId,
          offset: offsetValue,
          limit,
          isRefreshing: true,
        }),
      )
    }
  }, [clientId, refresh, unpaid])

  // Fetch initial data with proper invoice params when necessary
  useFetchOnInitialSyncQueryParams({
    key: 'Invoices',
    onFetch: () => {
      const offset = getExpandedWidgetOffset(
        initialItemParam,
        supportBiDirectionalInfiniteScroll,
      )
      const limit = getExpandedWidgetLimit(
        initialItemParam,
        supportBiDirectionalInfiniteScroll,
      )
      setOffsetValue(offset)
      dispatch(
        fetchInvoicePage({
          clientId,
          limit,
          offset,
          refundStateIds,
          stateIds: invoiceStateIds,
          unpaid,
          fillPrecedingWithNull:
            offset > 0 && supportBiDirectionalInfiniteScroll,
        }),
      )
    },
  })

  return (
    <ClientBillingActivityInfiniteLoaderList
      InfiniteLoaderListProps={InfiniteLoaderListProps}
      classes={classes}
      clientId={clientId}
      columns={sortedColumns}
      emptyPlaceholder={t('Common:NO_INVOICES')}
      error={error}
      initialItemParam={initialItemParam}
      isLoadingInitialData={unpaid ? isLoadingUnpaidInvoices : isLoading}
      pageData={unpaid ? unpaidInvoicesPageData : invoicesPageData}
      totalCount={unpaid ? unpaidInvoicesTotalCount : invoicesTotalCount}
      onFetchMore={handleFetchMore}
    />
  )
}
